import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Puzzle, Eye, Plus, Pencil, Trash2, X, Search, ChevronDown } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls, btnCls, useConfig } from "./crud";

const STATUS_STYLES = {
  "Reutilizable": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "En revisión": "bg-amber-50 text-amber-700 border-amber-200",
  "Experimental": "bg-violet-50 text-violet-700 border-violet-200",
  "Obsoleto": "bg-red-50 text-red-600 border-red-200",
  "Interno": "bg-slate-100 text-slate-600 border-slate-300",
};

const label = (t) => <label className="font-mono-tech text-[10px] tracking-widest text-slate-500 block mb-1">{t}</label>;
const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");

function StatusBadge({ status }) {
  return <span className={`inline-block border rounded-full px-2.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${STATUS_STYLES[status] || STATUS_STYLES["Interno"]}`}>{status || "—"}</span>;
}

function ReuseBar({ pct }) {
  const v = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${v}%` }} /></div>
      <span className="font-mono-tech text-[11px] text-emerald-600">{v}%</span>
    </div>
  );
}

function ModuleForm({ initial, config, onClose, onSaved }) {
  const [m, setM] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setM({ ...m, [k]: e.target ? e.target.value : e });
  const save = async () => {
    if (!m.name.trim() || !m.module_id.trim()) { toast.error("Nombre e ID técnico son obligatorios"); return; }
    setSaving(true);
    try {
      const { _id, created_at, updated_at, ...rest } = m;
      const data = { ...rest, reuse_pct: Number(m.reuse_pct) || 0, module_id: m.module_id.trim().toLowerCase(),
        dependencies: typeof m.dependencies === "string" ? m.dependencies.split(",").map((s) => s.trim()).filter(Boolean) : m.dependencies };
      if (_id) await api.put(`/admin/content/scme_modules/${_id}`, { data });
      else await api.post("/admin/content/scme_modules", { data });
      toast.success("Guardado"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-2xl max-h-[85vh] overflow-auto p-6" data-testid="scme-form-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-sm">{m._id ? "Editar módulo" : "Nuevo módulo"} — SCME</h3>
          <button onClick={onClose} data-testid="scme-form-close"><X size={16} className="text-slate-500" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>{label("NOMBRE DEL MÓDULO")}<input className={inputCls} value={m.name} onChange={set("name")} data-testid="scme-field-name" /></div>
          <div>{label("ID TÉCNICO")}<input className={inputCls} placeholder="ej: auth-jwt" value={m.module_id} onChange={set("module_id")} data-testid="scme-field-module-id" /></div>
          <div>{label("CATEGORÍA")}
            <select className={inputCls} value={m.category} onChange={set("category")} data-testid="scme-field-category">
              {(config.scme_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>{label("ESTADO")}
            <select className={inputCls} value={m.status} onChange={set("status")} data-testid="scme-field-status">
              {(config.scme_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>{label("VERSIÓN")}<input className={inputCls} placeholder="v1.0" value={m.version} onChange={set("version")} data-testid="scme-field-version" /></div>
          <div>{label("PROYECTO ORIGEN")}<input className={inputCls} placeholder="ej: naturautoescuela.net" value={m.origin_project} onChange={set("origin_project")} data-testid="scme-field-origin" /></div>
          <div>{label("NIVEL DE REUTILIZACIÓN (%)")}<input type="number" min="0" max="100" className={inputCls} value={m.reuse_pct} onChange={set("reuse_pct")} data-testid="scme-field-reuse" /></div>
          <div>{label("ÚLTIMA REVISIÓN")}<input type="date" className={inputCls} value={m.reviewed_at || ""} onChange={set("reviewed_at")} data-testid="scme-field-reviewed" /></div>
          <div className="sm:col-span-2">{label("DESCRIPCIÓN")}<textarea rows={3} className={inputCls} value={m.description} onChange={set("description")} data-testid="scme-field-description" /></div>
          <div className="sm:col-span-2">{label("DEPENDENCIAS (SEPARADAS POR COMAS)")}
            <input className={inputCls} placeholder="ej: bcrypt, pyjwt" value={Array.isArray(m.dependencies) ? m.dependencies.join(", ") : m.dependencies} onChange={set("dependencies")} data-testid="scme-field-dependencies" />
          </div>
        </div>
        <div className="mt-6 flex justify-end"><button onClick={save} disabled={saving} className={btnCls} data-testid="scme-save-btn">{saving ? "..." : "GUARDAR"}</button></div>
      </div>
    </div>
  );
}

function ModuleDetail({ m, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-xl max-h-[85vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="scme-detail-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-base font-medium text-[#0B1220]">{m.name}</h3>
            <span className="font-mono-tech text-[11px] text-slate-400">{m.module_id}</span>
          </div>
          <button onClick={onClose} data-testid="scme-detail-close"><X size={16} className="text-slate-500" /></button>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="border border-slate-200 bg-slate-50 rounded-full px-2.5 py-0.5 text-[11px] text-slate-600">{m.category}</span>
          <StatusBadge status={m.status} />
          <span className="font-mono-tech text-[11px] text-slate-500">{m.version}</span>
        </div>
        <p className="text-sm text-slate-600 mb-4 whitespace-pre-wrap">{m.description || "Sin descripción."}</p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-xs border-t border-slate-100 pt-4">
          <div>{label("PROYECTO ORIGEN")}<span className="font-mono-tech text-slate-600">{m.origin_project || "—"}</span></div>
          <div>{label("REUTILIZACIÓN")}<ReuseBar pct={m.reuse_pct} /></div>
          <div>{label("DEPENDENCIAS")}<span className="text-slate-600">{(m.dependencies || []).length ? m.dependencies.join(", ") : "Ninguna"}</span></div>
          <div>{label("FECHAS")}<span className="text-slate-600">Creado {fmtDate(m.created_at)} · Revisado {fmtDate(m.reviewed_at)}</span></div>
        </div>
      </div>
    </div>
  );
}

export default function ScmePlugins() {
  const config = useConfig();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [status, setStatus] = useState("");
  const [origin, setOrigin] = useState("");
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/admin/content/scme_modules").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const origins = useMemo(() => [...new Set(items.map((i) => i.origin_project).filter(Boolean))].sort(), [items]);
  const filtered = useMemo(() => items.filter((i) => {
    const t = q.trim().toLowerCase();
    if (t && !`${i.name} ${i.module_id} ${i.category} ${i.description}`.toLowerCase().includes(t)) return false;
    if (cat && i.category !== cat) return false;
    if (status && i.status !== status) return false;
    if (origin && i.origin_project !== origin) return false;
    return true;
  }), [items, q, cat, status, origin]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este módulo del catálogo?")) return;
    await api.delete(`/admin/content/scme_modules/${id}`);
    toast.success("Eliminado"); load();
  };

  const blank = () => ({ name: "", module_id: "", category: (config?.scme_categories || [])[0] || "", version: "v1.0",
    status: (config?.scme_statuses || [])[0] || "", origin_project: "", reuse_pct: 0, description: "",
    dependencies: [], reviewed_at: new Date().toISOString().slice(0, 10) });

  if (!config) return null;
  const selectCls = inputCls.replace("w-full ", "") + " w-auto min-w-[180px]";

  return (
    <div className="mt-10 border border-slate-200 rounded-lg bg-white shadow-sm" data-testid="scme-section">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-6 py-4 text-left" data-testid="scme-toggle">
        <ChevronDown size={16} className={`text-slate-400 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
        <div>
          <span className="font-display text-sm font-medium text-[#0B1220]">Ecosistema de Plugins (SCME)</span>
          <span className="block text-[11px] text-slate-400">Catálogo solo-lectura de módulos reutilizables · SCME v0.2</span>
        </div>
      </button>
      {open && (
        <div className="px-6 pb-6">
          <div className="border border-slate-200 rounded-lg p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-xl">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <Puzzle size={16} className="text-[#5698C7]" />
                  <h2 className="font-display text-base font-semibold text-[#0B1220]">Ecosistema de Plugins</h2>
                  <span className="font-mono-tech text-[10px] tracking-wider bg-[#5698C7]/10 text-[#4587B8] border border-[#5698C7]/30 rounded px-1.5 py-0.5">SCME v0.2</span>
                  <span className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-wider bg-slate-50 text-slate-500 border border-slate-200 rounded px-1.5 py-0.5"><Eye size={10} /> Solo lectura</span>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Catálogo corporativo de módulos reutilizables del ecosistema. Ningún módulo se instala, exporta ni sincroniza desde aquí — es documentación viva. Los proyectos permanecen 100% independientes. Reutilización siempre manual y controlada.
                </p>
              </div>
              <div className="text-right space-y-2">
                <span className="block text-[11px] text-slate-400" data-testid="scme-count">{items.length} módulos catalogados</span>
                <button onClick={() => setEditing(blank())} className={btnCls} data-testid="scme-add-btn"><Plus size={14} /> AÑADIR MÓDULO</button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-5">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input className={inputCls + " pl-9"} placeholder="Buscar por nombre, ID, categoría o descripción..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="scme-search-input" />
              </div>
              <select className={selectCls} value={cat} onChange={(e) => setCat(e.target.value)} data-testid="scme-filter-category">
                <option value="">Todas las categorías</option>
                {(config.scme_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select className={selectCls} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="scme-filter-status">
                <option value="">Todos los estados</option>
                {(config.scme_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select className={selectCls} value={origin} onChange={(e) => setOrigin(e.target.value)} data-testid="scme-filter-origin">
                <option value="">Todos los proyectos</option>
                {origins.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    {["PLUGIN", "CATEGORÍA", "VERSIÓN", "ESTADO", "PROYECTO ORIGEN", "REUTILIZACIÓN", ""].map((h, i) => (
                      <th key={i} className="font-mono-tech text-[10px] tracking-widest text-slate-500 px-4 py-3 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-slate-400 text-center">{items.length === 0 ? "Catálogo vacío. Añade el primer módulo con AÑADIR MÓDULO." : "Sin resultados para los filtros aplicados."}</td></tr>}
                  {filtered.map((m) => (
                    <tr key={m._id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(m)} data-testid={`scme-row-${m._id}`}>
                      <td className="px-4 py-3">
                        <span className="block font-medium text-[#0B1220] text-[13px]">{m.name}</span>
                        <span className="font-mono-tech text-[10px] text-slate-400">{m.module_id}</span>
                      </td>
                      <td className="px-4 py-3"><span className="border border-slate-200 bg-slate-50 rounded-full px-2.5 py-0.5 text-[11px] text-slate-600 whitespace-nowrap">{m.category}</span></td>
                      <td className="px-4 py-3 font-mono-tech text-[11px] text-slate-600 whitespace-nowrap">{m.version}</td>
                      <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
                      <td className="px-4 py-3 font-mono-tech text-[11px] text-slate-500 whitespace-nowrap">{m.origin_project || "—"}</td>
                      <td className="px-4 py-3"><ReuseBar pct={m.reuse_pct} /></td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-3 justify-end">
                          <button onClick={() => setEditing({ ...m })} className="text-slate-500 hover:text-[#5698C7] transition-colors duration-200" data-testid={`scme-edit-${m._id}`}><Pencil size={14} /></button>
                          <button onClick={() => remove(m._id)} className="text-slate-500 hover:text-red-600 transition-colors duration-200" data-testid={`scme-delete-${m._id}`}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {editing && <ModuleForm initial={editing} config={config} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {detail && <ModuleDetail m={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
